import { t } from "../../../../../utils/i18n";
import React from "react";
import ActivateCardModal from "./ActivateCardModal";
import { cardImages } from "../../New/helpers";

import "../../List/CardRequest/index.scss";
import { getCurrentDisplayStatus } from "./helpers";

type CardStatus = "completed" | "active" | "processing" | "error";

interface Props {
  cardStatus: CardStatus;
  cardRequest: {
    id: string;
    user_id: string;
    name_on_card: string;
    status: CardStatus;
    card_type: string;
    can_activate_card: boolean;
  };
}

const ActivateCard: React.FC<Props> = ({ cardRequest, cardStatus }) => {
  const [activateModalIsOpen, setActivateModalIsOpen] = React.useState(false);

  const currentDisplayStatus = getCurrentDisplayStatus(
    cardStatus,
    cardRequest.status
  );
  const cardImageUrl =
    cardImages[cardRequest.card_type] ||
    "https://via.placeholder.com/400x250?text=Card+Image";
  const cardTypeDisplay = cardRequest.card_type
    ? cardRequest.card_type.replace(/_/g, " ").toUpperCase()
    : t("cards.details.card_fallback");

  let statusIcon = "fa-info-circle";
  let statusIconColor = "var(--text-light, #636e72)";

  if (["completed", "active"].includes(currentDisplayStatus.toLowerCase())) {
    statusIcon = "fa-check-circle";
    statusIconColor = "var(--success-color, #00b894)";
  } else if (
    ["processing", "pending"].includes(currentDisplayStatus.toLowerCase())
  ) {
    statusIcon = "fa-spinner fa-spin";
    statusIconColor = "var(--accent-color, #6c5ce7)";
  } else if (
    ["error", "failed", "rejected"].includes(currentDisplayStatus.toLowerCase())
  ) {
    statusIcon = "fa-times-circle";
    statusIconColor = "var(--danger-color, #e17055)";
  }

  return (
    <div className="w-100">
      <div className="modern-card-container modern-fade-in">
        <div
          className="modern-card-header"
          style={{ background: "var(--primary-color, #4c3f84)" }}
        >
          <i className="fa fa-check-circle"></i>
          <h5>{t("cards.activations.card_activation_caps")}</h5>
        </div>
        <img
          src={cardImageUrl}
          alt={t("cards.activations.card_alt", { type: cardTypeDisplay })}
          className="mt-3" 
          style={{
            width: "100%",
            height: "180px",
            objectFit: "contain",
            backgroundColor: "#ffffff",
          }}
        />
        <div className="modern-card-body">
          <div className="mb-3">
            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.requests.card_type_caps")}
            </p>
            <h5 className="font-weight-bold">{cardTypeDisplay}</h5>
          </div>

          <div className="mb-3">
            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.requests.name_on_card_caps")}
            </p>
            <p className="font-weight-bold">
              {cardRequest.name_on_card || t("cards.common.not_available")}
            </p>
          </div>

          <div className="mb-4">
            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.details.status_caps")}
            </p>
            <span
              style={{
                color: "var(--text-dark, #2d3436)",
                fontSize: "0.9rem",
                fontWeight: "500",
              }}
            >
              <i
                className={`fa ${statusIcon}`}
                style={{ marginRight: "0.5rem", color: statusIconColor }}
              ></i>
              {currentDisplayStatus.toUpperCase()}
            </span>
          </div>

          <button
            className="modern-btn modern-btn-primary w-100 mb-3"
            onClick={(e) => {
              e.preventDefault();
              setActivateModalIsOpen(true);
            }}
            disabled={!cardRequest.can_activate_card}
          >
            <i className="fa fa-bolt"></i> {t("cards.activations.activate_card")}
          </button>
        </div>
      </div>

      <ActivateCardModal
        cardRequestId={cardRequest.id}
        userId={cardRequest.user_id}
        isModalOpen={activateModalIsOpen}
        onClose={() => setActivateModalIsOpen(false)}
      />
    </div>
  );
};

export default ActivateCard;
